import { Box, Typography, Divider, Button, CircularProgress, Chip, Dialog, DialogContent, IconButton, useTheme, Paper } from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { Link, useNavigate, useParams } from 'react-router-dom'
import PublishIcon from '@mui/icons-material/Publish'
import RefreshIcon from '@mui/icons-material/Refresh'
import GetAppIcon from '@mui/icons-material/GetApp'
import React, { useEffect, useState } from 'react'
import EditIcon from '@mui/icons-material/Edit'
import InfoIcon from '@mui/icons-material/Info'
import Edit from '@mui/icons-material/Edit'
import { toast } from 'react-toastify'
import { convert } from 'html-to-text'
import parse from 'html-react-parser'
import { observer } from 'mobx-react'

import { FLAT_COLORS, chipColors, validateQuestionsImportFormat, calculateDurationBetween } from '../../utils/exam-utilities'
import QuestionImportFileInputDialog from '../../components/exams-components/QuestionImportFileInputDialog'
import QuestionsPreviewDialog from '../../components/exams-components/QuestionsPreviewDialog'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import BreadcrumbList from '../../components/common/BreadcrumbList'
import { useLayoutContext } from '../../contexts/layout.context'
import ConfirmDialog from '../../components/ConfirmDialog'
import { useUserStore } from '../../store/user.store'
import { useExamStore } from '../../store/exam.store'
import { EditableMathField } from 'react-mathquill'
import api from '../../service/api'
import { formatDate } from '../../utils/utilities'

const Exam = observer( () => {
    const [loadingBatchesAndSections, setLoadingBatchesAndSections] = useState( false )
    const [deleteDialogState, setDeleteDialogState] = useState( false )
    const [importDialogStatus, setImportDialogStatus] = useState( false )
    const [questionsPreview, setQuestionsPreview] = useState( null )
    const [deletingExam, setDeletingExam] = useState( false )
    const [isImporting, setIsImporting] = useState( false )
    const [inProgress, setInProgress] = useState( true )
    const [questions, setQuestions] = useState( null )
    const [notFound, setNotFound] = useState( false )
    const [batches, setBatches] = useState( null )

    const { setCurrentLoc } = useLayoutContext()

    let { id } = useParams()
    const navigate = useNavigate()
    const UserStore = useUserStore()
    const ExamStore = useExamStore()
    const { palette, border } = useTheme()

    // addStyles()

    const getExam = async () => {
        try {
            await ExamStore.fetchExam( id )
            setInProgress( false )
        } catch ( e ) {
            toast.error( "Something went wrong! try again later" )
            navigate( UserStore.getUser['user_role'] === "STUDENT" ? "/student/exams" : "/faculty/exams" )
        }
    }

    const publish = async () => {
        try {
            await api.publish( ExamStore.getExam.exam.exam_id )
            await getExam()
            toast.success( "Exam published!" )
        } catch ( e ) {
            console.log( e )
            toast.error( e.response.data?.message || "Failed to publish the E-mail." )
        }
    }

    const deleteExam = async () => {
        try {
            setDeletingExam( true )
            await api.delete( ExamStore.getExam.exam.exam_id )
            toast.success( <div><span style={{ color: "#f18f08" }}>{ExamStore.getExam.exam.exam_name}</span> successfully deleted</div> )
            navigate( "/faculty/exams" )
        } catch ( e ) {
            toast( e?.response?.data?.message || "Something went wrong! try again later" )
        } finally {
            setDeleteDialogState( false )
            setDeletingExam( true )
        }
    }

    const attempt = async () => {
        try {
            const res = await api.attempt( ExamStore.getExam.exam.exam_id )
            if ( res.status === 200 ) {
                if ( !res.data.isNewParticipation ) toast.success( "Proceeding with the previously unfinished exam! finish this to start new attampt" )
                navigate( `/student/exams/${ExamStore.getExam.exam.exam_id}/attempt/${res.data.attempt}/${res.data.participation_id}` )
            }
        } catch ( e ) {
            console.log( e.response.data )
            toast.error( e.response.data?.message || e.response.data || "Something went wrong! try again later" )
        }
    }

    const onImportChange = async ( e ) => {
        if ( e.target.files[0].name.includes( ".txt" ) ) {
            setQuestionsPreview( { status: "loading" } )
            const fileReader = new FileReader()
            fileReader.onload = () => {
                const { questions, errors, preview, errorLines } = validateQuestionsImportFormat( fileReader.result, "txt" )
                setQuestionsPreview( { status: "loaded", data: preview, file: e.target.files[0], type: "txt", plainData: fileReader.result, errors, questions, errorLines } )
                e.target.value = ""
            }
            fileReader.readAsText( e.target.files[0] )
        } else {
            try {
                const fileReader = new FileReader()
                fileReader.onload = async () => {
                    const formData = new FormData()
                    formData.append( 'file', e.target.files[0] )
                    try {
                        const { data: { data } } = await api.convertDocxToHtml( formData )
                        const { questions, errors, preview, errorLines } = validateQuestionsImportFormat( data, "docx" )
                        setQuestionsPreview( { status: "loaded", data: preview, file: e.target.files[0], type: "doc", plainData: convert( fileReader.result ), errors, questions, errorLines } )
                        e.target.value = ''
                    } catch ( err ) {
                        console.log( err )
                        toast( "Something went wrong! try again later" )
                    }
                }
                fileReader.readAsText( e.target.files[0] )
            } catch ( err ) {
                console.log( err )
            }
        }
        setImportDialogStatus( false )
    }
    const getQuestions = async () => {
        try {
            const { data: questionsRes } = await api.fetchQuestions( id, { forediting: true } )
            setQuestions( questionsRes.data || questionsRes )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong" )
        }
    }

    useEffect( () => {
        const getExam = async () => {
            try {
                const { success } = await ExamStore.fetchExam( id )
                if ( !success )
                    setNotFound( true )

                if ( UserStore.getUser["user_role"] === 'FACULTY' || UserStore.getUser["user_role"] === 'HOD' || UserStore.getUser["user_role"] === 'PRINCIPAL' || UserStore.getUser["user_role"] === 'presenter' ) {
                    const { data: questionsRes } = await api.fetchQuestions( id )
                    setQuestions( questionsRes.data )
                }
                console.log( ExamStore.getExam.exam.exam_attachments )
                setBatches( ExamStore.getExam.exam.batches_ids )
                setInProgress( false )
                if ( UserStore.getErrorsStatuses?.academicYearsAndBatches ) {
                    setLoadingBatchesAndSections( true )
                    await UserStore.fetchUsersAllBatchesAndSectionList()
                    setLoadingBatchesAndSections( false )
                }
            } catch ( e ) {
                console.log( e )
                if ( e.response && e.response.data ) {
                    if ( e.response.status === 404 && e.response.data === "Exam not found!" ) {
                        setNotFound( true )
                    } else
                        toast.error( e.response.data )
                }
                else toast.error( "Something went wrong!" )
                // navigate( '/exams' )
            }
        }
        getExam()
    }, [ExamStore, UserStore, id, navigate] )

    return (
        // <Layout>
        <Box height="100%" padding="20px" overflow="auto" >
            <Dialog open={notFound}>
                <DialogContent>
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <Typography variant='h3'>404</Typography>
                        <Typography marginTop="10px">Exam you are looking for doen't exists!</Typography>
                        <Button onClick={() => navigate( UserStore.getUser['user_role'] === "STUDENT" ? "/student/exams" : "/faculty/exams", { replace: true } )} color='error'>Back to exams</Button>
                    </Box>
                </DialogContent>
            </Dialog>
            <QuestionImportFileInputDialog questionsPreview={questionsPreview} isImporting={isImporting} importDialogStatus={importDialogStatus} setImportDialogStatus={setImportDialogStatus} onImportChange={onImportChange} />
            <QuestionsPreviewDialog examId={id} getQuestions={getQuestions} setQuestionsPreview={setQuestionsPreview} questionsPreview={questionsPreview} />
            <ConfirmDialog waiting={deletingExam} status={deleteDialogState} confirmAction={deleteExam} cancelAction={() => setDeleteDialogState( false )} actionName="Delete" message={<Typography>Do you really want to delete the exam <b>{ExamStore.getExam?.exam?.exam_name}</b> </Typography>} />

            {/* <Typography marginBottom="10px" fontSize="14px" fontWeight={500} display="flex" alignItems="center" color="textSecondary"> <Link to={UserStore.getUser['user_role'] === "STUDENT" ? "/student/exams" : "/faculty/exams"} className="breadcrumb-link">Exams</Link> {Icons.default.ChevronRightIcon} {ExamStore.getExam.exam && ExamStore.getExam.exam.exam_name ? ExamStore.getExam.exam.exam_name : <CircularProgress sx={{ color: "textSecondary" }} size={12} />}</Typography> */}
            <BreadcrumbList items={[
                { link: UserStore.getUser['user_role'] === "STUDENT" ? "/student/exams" : "/faculty/exams", label: "Exams" },
                ExamStore.getExam.exam ? ExamStore.getExam.exam.exam_name : <CircularProgress size={14} />
            ]} />

            {!inProgress && ExamStore.getExam.exam && <>
                <Paper>
                    <Box style={{ minHeight: "200px", display: "flex", alignItems: "flex-end", padding: "20px", color: "white", borderRadius: "5px 5px 0 0" }} sx={{ background: FLAT_COLORS[+( ExamStore.getExam.exam?.id || 1 ) % FLAT_COLORS.length] }} >
                        <Box>
                            <Typography variant='h4' >
                                {ExamStore.getExam.exam.exam_name}<small style={{ fontWeight: "bold", fontSize: "10px", marginLeft: "5px", letterSpacing: "1px", color: "white", textTransform: "uppercase" }}>{ExamStore.getExam.exam.exam_status}</small>
                            </Typography>
                            <Typography gutterBottom variant='body2' color='white' >{`${formatDate( ExamStore.getExam.exam.starts_at, { format: "LLL" } )} - ${formatDate( ExamStore.getExam.exam.ends_at, { format: "LLL" } )}`}</Typography>
                            {ExamStore.getExam.exam.exam_duration !== "null" && ExamStore.getExam.exam.exam_duration !== null && <Typography variant='body2' color='white' > {`Duration: ${ExamStore.getExam.exam.exam_duration.split( ":" )[0]} Hours ${ExamStore.getExam.exam.exam_duration.split( ":" )[1]} Minutes`}</Typography>}
                            {( ExamStore.getExam.exam.exam_duration === "null" || ExamStore.getExam.exam.exam_duration === null ) && <Typography variant='body2' color='white' > {`Duration: ${calculateDurationBetween( ExamStore.getExam.exam.starts_at, ExamStore.getExam.exam.ends_at )}`}</Typography>}
                            <Typography variant='body2' color='white' >Maximum Attempts: {ExamStore.getExam.exam.maximum_attempts}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", "@media(max-width:800px)": { flexDirection: "column", alignItems: "flex-start", flexWrap: "wrap" } }} padding="20px 10px" paddingBottom={UserStore.getUser["user_role"] !== 'STUDENT' ? "0" : "20px"}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                            {UserStore.getUser["user_role"] === 'STUDENT' && <Box display="flex" gap="10px" title={new Date( ExamStore.getExam.exam.ends_at ) < new Date() ? "Exam already ended" : ""}>
                                <Button disableElevation variant='contained' onClick={attempt} disabled={new Date( ExamStore.getExam.exam.ends_at ) < new Date()}>
                                    Attempt test
                                </Button>
                                {new Date( ExamStore.getExam.exam.ends_at ) < new Date() && <Button disableElevation sx={{ "&:hover": { color: "white !important" } }} onClick={() => { navigate( '/student/scores' ); setCurrentLoc( `/student/scores` ) }} variant='contained'>
                                    View scores
                                </Button>}
                            </Box>}
                            {UserStore.getUser["user_role"] !== 'STUDENT' && <Box flexWrap="wrap" display="flex" gap="10px" alignItems="center">
                                <Button disableElevation color='primary' type="button" disabled={( new Date( ExamStore.getExam.exam.starts_at ) < new Date() || isImporting || ExamStore.getExam.exam.exam_status === 'Published' )} startIcon={<GetAppIcon />} onClick={() => setImportDialogStatus( true )} variant='contained'>{isImporting ? "Importing Questions..." : "Import Questions"}</Button>
                                <Button disableElevation disabled={( new Date( ExamStore.getExam.exam.starts_at ) < new Date() || ExamStore.getExam.exam.exam_status === 'Published' )} onClick={() => navigate( `/faculty/exams/${ExamStore.getExam.exam.exam_id}/addquestions` )} sx={{ "&:hover": { color: "white" } }} variant='contained' startIcon={<AddCircleOutlineIcon />}>Add questions </Button>
                                <Button disableElevation disabled={( new Date( ExamStore.getExam.exam.starts_at ) < new Date() || ExamStore.getExam.exam.exam_status === 'Published' )} sx={{ "&:hover": { color: "white" } }} title="Edit ExamStore.getExam.exam" variant='contained' startIcon={<EditIcon />} onClick={() => navigate( `/faculty/exams/${ExamStore.getExam.exam.exam_id}/edit` )}> Edit</Button>
                                <Button disableElevation startIcon={<InfoIcon />} onClick={() => { navigate( `/faculty/exams/${ExamStore.getExam.exam.exam_id}/participation` ); setCurrentLoc( `/faculty/participations` ) }} sx={{ "&:hover": { color: "white" } }} variant='contained'>Details</Button>
                                {ExamStore.getExam.exam.exam_status.toLowerCase() !== "published" &&
                                    <Button startIcon={<PublishIcon />} disableElevation variant="contained" onClick={publish} color='secondary'>Publish</Button>}
                                <Button disableElevation variant='contained' startIcon={<DeleteForeverIcon />} onClick={() => setDeleteDialogState( true )} color='error'>Delete </Button>
                            </Box>}
                        </Box>
                    </Box>
                    {UserStore.getUser["user_role"] !== 'STUDENT' && <Box>
                        <i style={{ fontSize: "14px", display: "block", margin: "10px" }} >To know about the import file format <Link to='https://docs.moodle.org/401/en/Aiken_format' target="_blank" style={{ color: "purple", fontWeight: "bold", cursor: "pointer", textDecoration: "underline !important" }} >Aiken click here.</Link> </i>
                    </Box>}
                    <Box padding="0 20px 20px 20px">
                        <Divider sx={{ marginBottom: "10px", marginTop: "10px", background: "#273679", height: "1px" }} />
                        <p style={{ textAlign: "justify", fontSize: "14px" }}>
                            {ExamStore.getExam.exam.exam_description}
                        </p>
                        <Typography variant='h6'>Topics</Typography>
                        <Box sx={{ padding: "10px 0", display: "flex", flexWrap: "wrap", }} flexWrap="wrap" gap="5px">
                            {ExamStore.getExam.exam.exam_topics && ExamStore.getExam.exam.exam_topics.map( ( topic, index ) => (
                                <Chip key={topic} label={topic} sx={{ background: chipColors[index % FLAT_COLORS.length], color: "white" }} />
                            ) )}
                        </Box>
                        {UserStore.getUser["user_role"] !== 'STUDENT' && ExamStore.getExam?.exam?.batches_ids?.length > 0 && <Box marginY="10px">
                            <Typography marginBottom="5px" variant='h6'>Exam for batch(es):</Typography>
                            {!loadingBatchesAndSections && <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center">
                                {batches && batches.map( ( batch, index ) => {
                                    const access = UserStore.getUsersAllBatchesAndSectionList?.find( i => ( +i.batch_assignment_id === +batch || `${i.section_assignment_id}-${i.section_short_name}` === String( batch ) ) )
                                    let title = ''
                                    if ( access?.section_assignment_id ) {
                                        title = <Box key={batch} sx={{ padding: "5px 10px", marginRight: "5px", borderRadius: "5px", border: border[1], bgcolor: palette.greyedOut, fontWeight: "600" }} >
                                            <Typography fontSize="12px" color="inherit" fontWeight="inherit">{`${access.course_branch_short_name ? access.course_branch_short_name?.trim() + "," : ""} ${access?.current_year ? `year: ${access?.current_year},` : ""} ${access.current_sem ? `Sem: ${access.current_sem},` : ""} Sec: ${access.section_short_name}`}</Typography>
                                            <Typography fontSize="10px" color="inherit" fontWeight="inherit">{access.ac_year}</Typography>
                                        </Box>
                                    } else if ( access?.batch_assignment_id )
                                        title = <Box key={batch} sx={{ padding: "5px 10px", marginRight: "5px", borderRadius: "5px", border: border[1], bgcolor: palette.greyedOut, fontWeight: "600" }} >
                                            <Typography fontSize="12px" color="inherit" fontWeight="inherit">{`${access.course_name_short ? access.course_name_short + "," : ""} ${access?.current_year ? `year: ${access?.current_year},` : ""} ${access.current_sem ? `Sem: ${access.current_sem},` : ""} Batch: ${access.batch_short_name}`}</Typography>
                                            <Typography fontSize="10px" color="inherit" fontWeight="inherit">{access.ac_year}</Typography>
                                        </Box>
                                    return (
                                        title
                                    )
                                } )}
                                {( !batches || batches.length === 0 ) && <Typography variant='body2' color="textSecondary">Exam is not yet assigned to batches!</Typography>}
                            </Box>}
                            {loadingBatchesAndSections && <DataLoadingSpinner waitingMessage="Loading batches and sections..." />}
                        </Box>}
                        {/* <Typography variant='h6'>Attachments<AttachmentIcon sx={{ margin: "-2px 0 0 5px" }} fontSize="small" /> </Typography>
                        <Box sx={{ padding: "10px 0", display: "flex", flexWrap: "wrap", }} flexWrap="wrap" gap="10px">
                            {ExamStore.getExam.exam.exam_attachments && ExamStore.getExam.exam.exam_attachments.map( ( attachment ) => (
                                <Chip href={`${ATTACHMENTS_BASE_URL}${attachment}`} clickable deleteIcon={<DownloadIcon />} onDelete={e => window.open( `${ATTACHMENTS_BASE_URL}${attachment}` )} sx={{ fontWeight: "bold", fontSize: "12px", padding: "5px", border: "1px solid grey", marginRight: "5px", display: "flex" }} label={attachment.split( "exam/" )[1]} key={attachment} />
                            ) )}
                            {!ExamStore.getExam.exam.exam_attachments &&
                                <Typography variant='body2' color="textSecondary" mt={-1}>No attachments provided!</Typography>
                            }
                        </Box> */}

                        {UserStore.getUser["user_role"] !== 'STUDENT' && <Box>
                            <Box display="flex" justifyContent="space-between" marginTop="10px" flexWrap="wrap" gap="10px">
                                <Typography variant='h6'>Exam questions</Typography>
                                <Box display="flex" gap="20px">
                                    <IconButton onClick={async () => {
                                        const { data: questionsRes } = await api.fetchQuestions( id )
                                        setQuestions( questionsRes.data )
                                    }} size="small" color='primary'>
                                        <RefreshIcon sx={{ fontSize: "20px" }} />
                                    </IconButton>
                                    {!( new Date( ExamStore.getExam.exam.exam_start_time ) < new Date() || ExamStore.getExam.exam.exam_status === 'Published' ) && questions && questions.length > 0 && <Button onClick={() => navigate( `/faculty/exams/${ExamStore.getExam.exam.exam_id}/questions` )} sx={{ fontSize: "14px", textTransform: "capitalize" }} startIcon={<Edit sx={{ fontSize: "16px !important" }} />} >Edit questions</Button>}
                                </Box>
                            </Box>
                            {questions && questions.length > 0 && <Box display="flex" marginTop="10px" flexDirection="column" gap="10px">
                                {questions.map( ( question, index ) => (
                                    <Box sx={{ display: "flex", alignItems: "center", padding: "20px", background: "white", boxShadow: "0 -2px 4px 0 rgba(0, 0, 0, 0.2), 0 2px 4px 0 rgba(0, 0, 0, 0.19)", gap: "10px", fontSize: "14px", borderRadius: "5px", "& p": { margin: "0 " } }} key={question.question.question_id}>
                                        <span style={{ display: "flex", borderRadius: "50%", alignItems: "center", justifyContent: "center", minWidth: "30px", maxWidth: "30px", minHeight: "30px", maxHeight: "30px", background: "rgb(48,48,48)", color: "white" }}>{index + 1}</span>
                                        <Box>
                                            <Box>
                                                {question.question.question_text.trim().includes( 'LATEX-' ) ? <EditableMathField latex={question.question.question_text.trim().split( "LATEX-" )[1]} id='static-latex' style={{ padding: "0px", fontFamily: "'Rubik','Roboto', sans-serif !important" }}> </EditableMathField> : parse( question.question.question_text.trim() )}
                                            </Box>
                                            <Box marginTop="5px">
                                                <Chip size='small' color='secondary' sx={{ marginLeft: "5px" }} label={question.question.question_type}></Chip>
                                                {question.question.question_type === 'MCQ' && <Chip size='small' color='secondary' sx={{ marginLeft: "5px" }} label={question.question.question_answer_selection}></Chip>}
                                            </Box>
                                        </Box>
                                    </Box>
                                ) )}
                            </Box>}
                            {( !questions || questions.length === 0 ) && <Typography color="textSecondary" variant='body2'>Questions not yet added!</Typography>}
                        </Box>
                        }
                    </Box>
                </Paper>
            </>
            }
            {inProgress &&
                <Typography height="100%" borderRadius="5px" padding="20px" boxShadow="0 1px 2px 0 rgba(0, 0, 0, 0.2), 0 2px 4px 0 rgba(0, 0, 0, 0.19)" variant="h6" bgcolor="white" color="textSecondary"><CircularProgress size={20} sx={{ marginRight: "10px" }} />Loading...  </Typography>
            }
        </Box>
        // </Layout >
    )
} )

export default Exam